import { useState, useEffect } from "react";
import { BarChart3, Eye, TrendingUp, Calendar, Globe } from "lucide-react";
import { getAnalyticsSummary } from "../lib/api";
import { toast } from "sonner";

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await getAnalyticsSummary();
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const maxDailyVisits = analytics?.daily_visits?.length > 0 
    ? Math.max(...analytics.daily_visits.map(d => d.count)) 
    : 1;

  const maxPageVisits = analytics?.visits_by_page?.length > 0
    ? Math.max(...analytics.visits_by_page.map(p => p.count))
    : 1;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
          Analytics
        </h1>
        <p className="text-muted-foreground mt-1">
          Track visitor activity on your public tournament page
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Eye className="w-5 h-5 text-primary" />
            </div>
            <span className="text-sm text-muted-foreground uppercase font-semibold">Total Visits</span>
          </div>
          <p className="text-4xl font-heading font-black">{analytics?.total_visits || 0}</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <span className="text-sm text-muted-foreground uppercase font-semibold">Today</span>
          </div>
          <p className="text-4xl font-heading font-black">{analytics?.today_visits || 0}</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Globe className="w-5 h-5 text-blue-500" />
            </div>
            <span className="text-sm text-muted-foreground uppercase font-semibold">Unique Pages</span>
          </div>
          <p className="text-4xl font-heading font-black">{analytics?.visits_by_page?.length || 0}</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Calendar className="w-5 h-5 text-purple-500" />
            </div>
            <span className="text-sm text-muted-foreground uppercase font-semibold">Active Days</span>
          </div>
          <p className="text-4xl font-heading font-black">{analytics?.daily_visits?.length || 0}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Visits Chart */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Daily Visits (Last 30 Days)
          </h2>
          {analytics?.daily_visits?.length > 0 ? (
            <div className="space-y-2">
              {analytics.daily_visits.slice(-14).map((day, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-20 shrink-0">
                    {new Date(day.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                  <div className="flex-1 h-6 bg-secondary rounded overflow-hidden">
                    <div
                      className="h-full bg-primary rounded transition-all duration-500"
                      style={{ width: `${(day.count / maxDailyVisits) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-mono font-bold w-8 text-right">{day.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No visit data yet</p>
            </div>
          )}
        </div>

        {/* Top Pages */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Top Pages
          </h2>
          {analytics?.visits_by_page?.length > 0 ? (
            <div className="space-y-3">
              {analytics.visits_by_page.slice(0, 10).map((page, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-lg font-bold text-muted-foreground w-6">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium truncate">{page.page || "/"}</span>
                      <span className="text-sm font-mono font-bold">{page.count}</span>
                    </div>
                    <div className="h-2 bg-secondary rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-primary/50 rounded transition-all duration-500"
                        style={{ width: `${(page.count / maxPageVisits) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Globe className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No page data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-secondary/50 border border-border rounded-xl p-4 text-sm text-muted-foreground">
        <p>
          <strong>Note:</strong> Analytics are tracked when visitors access your public tournament page. 
          Share your public URL to start collecting visitor data.
        </p>
      </div>
    </div>
  );
}
